/*************************************************************************
 *
 * KLEARNOW CONFIDENTIAL
 * __________________
 *
 *  Copyright (c) 2018 - 2020 KLEARNOW Corporation.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of KLEARNOW Corporation and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to KLEARNOW Corporation
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from KLEARNOW Corporation.
 */
import { formatHTS } from '../util'
import moment from 'moment';
import { showToast } from "../util/showToast";
import { deletePgaFormData } from './deleteFormData';
import { HttpClient } from './httpClient';
import { buildIDBCacheKey, idbGetHtsMetadata, idbSetHtsMetadata } from './htsIDBCache';

export const getHTSData = (code,mycountry,epochs) => {
  const htsCode = formatHTS(code);
  const cacheKey = `GLOBAL_HTS_TREE_CACHE_${mycountry}_${epochs || 0}_${htsCode}`;
  if (window[cacheKey]) return window[cacheKey];

  const data = {
    eventMessage: {
      country: mycountry,
       htsCode,
       eta: epochs
    },
    eventType: 'GET_HTS_TREE',
    eventTime: Date.now()
  }
  const request = HttpClient.custom({
    data
  }).then((response) => {
    if (!response?.data?.eventMessage?.htsCode) {
      delete window[cacheKey];
    }
    return response;
  }).catch((err) => {
    delete window[cacheKey];
    throw err;
  });
  window[cacheKey] = request;
  return request;
}

export const getHSDtlList_old = htsArray => {
  const data = {
    eventMessage: {
      hsList: htsArray.map(hts => ({
        country: 'US',
        hsVersion: '2019Rev2.0',
        hsCodeRoot: hts
      })),
      country: 'US',
      hsVersion: '2019Rev2.0'
    },
    eventType: 'GET_HS_DTL_LIST_HS',
    eventTime: Date.now()
  }
  return HttpClient.custom({
    data
  })
}

export const deleteUnusedPGAS = (pgasFlagsToShowOnUi, pgasFlagsToDelete,  merchandiseId, shipmentId, lineItemIndex, invoiceItemIndex,
  updateMerchLineItemPgaObject,updateMerchLineItemFeild, doittwice) => {


  let deletionResponse = deletePgaFormData(pgasFlagsToDelete, merchandiseId, shipmentId)
  
  // console.log('currentFormsSelected: ', currentFormsSelected);
  updateMerchLineItemFeild(lineItemIndex, invoiceItemIndex, "selectedFormTypes", pgasFlagsToShowOnUi.map(a=>a.pgaType))
  updateMerchLineItemFeild(lineItemIndex, invoiceItemIndex, "pgaFlagInfos", pgasFlagsToShowOnUi)
  return deletionResponse;
  
}

export const getHSDtlList = (itDate, allEntryData, hts, countryOfOrigin, preloadedMetadata, merchandiseDutyId, options = {}) => {
  const { omitEntryDate = false } = options;
  if(typeof allEntryData == 'number'){
    showToast("Invalid entrytype info! Report this as a bug immediately!");
  }
  // Priority: IT Date first, then Release Date, then Entry Date
  let entryDateOrReleaseDate = itDate || 0;
  if(entryDateOrReleaseDate < 1000){
    entryDateOrReleaseDate = allEntryData?.releaseDate?.epochs || 0;
  }
  if(entryDateOrReleaseDate < 1000){
    entryDateOrReleaseDate = allEntryData?.entryDate?.epochs || 0;
  }
  if(entryDateOrReleaseDate < 1000){
    entryDateOrReleaseDate = 0;
  }
  if(!entryDateOrReleaseDate){
    showToast("IT Date, Release Date and Entry Date are missing!");
  }

  // L1: in-memory window cache (per session, expires every 3 hours)
  const entryDateFormatted = omitEntryDate ? 'nodate' : moment.utc(entryDateOrReleaseDate).format("MM-DD-YYYY");
  let cacheName = `GLOBAL_CURRENTDETAILSCACHE_${Math.floor(Date.now()/ (1000 *60 *60 * 3))}_${countryOfOrigin}_${entryDateOrReleaseDate}_${hts}`;
  if(window[cacheName]) return window[cacheName];

  // If preloaded metadata is provided, populate both caches and return immediately
  if (preloadedMetadata) {
    const preloadedPromise = Promise.resolve(preloadedMetadata);
    window[cacheName] = preloadedPromise;
    return preloadedPromise;
  }

  // L2: IndexedDB cache (persists across page loads for 24 hours)
  const idbKey = buildIDBCacheKey(hts, countryOfOrigin, entryDateFormatted);

  const responseForDetail = idbGetHtsMetadata(idbKey).then((cached) => {
    if (cached) {
      // Warm the L1 cache so subsequent same-session calls are synchronous
      window[cacheName] = Promise.resolve(cached);
      return cached;
    }

    // L3: Network call
    const requestKey = merchandiseDutyId || `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const data = {
      eventMessage: {
        requestMap: {
          [requestKey]: {
            country: "US",
            htsCode: hts,
            ...(!omitEntryDate && { entryDate: entryDateFormatted }),
            countryOfOrigin,
          }
        }
      },
      eventType: "GET_HTS_METADATA_US",
      eventTime: Date.now()
    };

    return HttpClient.custom({ data }).then((resp) => {
      const responseMap = resp?.data?.eventMessage?.responseMap || {};
      const primaryHtsMetadata = responseMap[requestKey]?.primaryHtsMetadata;
      const result = { data: { eventMessage: primaryHtsMetadata } };

      // Persist to IndexedDB for future sessions (fire-and-forget)
      idbSetHtsMetadata(idbKey, result);

      return result;
    });
  });

  // Store the promise in L1 immediately so parallel calls within the same
  // session deduplicate against it even while the IDB/network round-trip is in flight
  window[cacheName] = responseForDetail;
  return responseForDetail;
}

export const getHtsMetadataBatch = (payload) => {
  // Extract from old payload format (data.eventMessage) or use payload as individual params
  const eventMessage = payload?.data?.eventMessage;
  const merchandiseDutyId = payload?.data?.merchandiseDutyId;
  
  const uniqueId = merchandiseDutyId || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  const requestMapEntry = {
    htsCode: eventMessage.htsCode,
    countryOfOrigin: eventMessage.countryOfOrigin,
    tradeAgreement: eventMessage.tradeAgreement,
    ...(eventMessage.smeltCountryOfOrigin && { smeltCountryOfOrigin: eventMessage.smeltCountryOfOrigin }),
    ...(eventMessage.entryDate && { entryDate: eventMessage.entryDate }),
    customConfig: eventMessage?.customConfig
  };
  
  return HttpClient.custom({
    data: {
      eventMessage: {
        requestMap: {
          [uniqueId]: requestMapEntry
        }
      },
      eventType: 'GET_HTS_METADATA_US',
      eventTime: Date.now()
    }
  });
}

export const getPGATemplate = (formType) => {
  const data = {
    "eventMessage": {
        formType
    },
    "eventType": "GET_PGA_TEMPLATE",
    "eventTime": Date.now()
  }


  return HttpClient.custom({
    data
  })
}



/*
export const getHSDtl = hts => {
  const data = {
    eventMessage: {
      hsCodeRoot: hts,
      country: 'US',
      hsVersion: '2019Rev2.0'
    },
    eventType: 'GET_HS_DTL_HS',
    eventTime: Date.now()
  }
  return HttpClient.custom({
    data
  })
}
*/

export const getHSTotalDCT = (hsList, entryTime, calMode, totHMF, totMPF, totDuty, totTax, totalUserFees) => {
  let data = {
    eventMessage: {
      country: 'US',
      hsVersion: '2019Rev2.0',
      calMode,
      entryTime: entryTime ? entryTime.toString() : Date.now().toString()
    },
    eventType: 'GET_HS_TOTAL_DCT_HS',
    eventTime: Date.now()
  }
  if (!hsList) {
    data.eventMessage.totHMF = totHMF
    data.eventMessage.totMPF = totMPF
    data.eventMessage.totDuty = totDuty
    data.eventMessage.totTax = totTax
    data.eventMessage.totUserFee = totalUserFees
  } else {
    data.eventMessage.hsList = hsList
  }
  return HttpClient.custom({
    data
  })
}

export const gethsDataDtlListBody = (params) => {
  let {
    uiValue,
    htsCode,
    tradeAgreement,
    modeOfTransport,
    enteredValue,
    entryType,
    portEntry,
    portOfUnlading,
    countryOfOrigin,
    itemId,
    // OverWrite Fields
    hmfVal,
    mpfVal,
    duty,
    dutyRate
  } = params
  if (tradeAgreement === 'NONE') {
    tradeAgreement = null
  }
  let result = {
    country: 'US',
    hsCodeRoot: htsCode,
    hsVersion: '2019Rev2.0',
    hsTrade: tradeAgreement,
    countryOrigin: countryOfOrigin,
    modeTransport: modeOfTransport,
    value: enteredValue,
    entryType,
    portEntry,
    portUnLading: portOfUnlading,
    uiValue,
    itemId
  }
  if (uiValue === 'Y') {
    return Object.assign(result, { dutyRate, hmfVal, mpfVal, duty })
  } else {
    return result
  }
}